import Button from "@/components/Button";
import { arrow, pitchCardPlaceholder } from "@/designSystemIcons";
import { cn } from "@/lib/utils";
import { EvolutionBuilderPlayer } from "@/redux/evolutions/evolutionsBuilderSlice";
import { PlayStylesLabRecommendation } from "@/redux/playStylesLab/playStylesLabSlice";
import { useTranslation } from "@repo/shared/i18n";
import { Save, Share, Trash2 } from "lucide-react";
import { useCallback, useState } from "react";
import { Oval } from "react-loading-icons";

import { PlayStyleRecommendationCard } from "./PlayStyleRecommendationCard";

type Props = {
  recommendations: PlayStylesLabRecommendation[];
  player: EvolutionBuilderPlayer | null;
  onApplyRecommendation?: (recommendation: PlayStylesLabRecommendation) => void;
  loading?: boolean;
  onSave?: () => void;
  saving?: boolean;
  onShare?: () => void;
  onClear?: () => void;
};

export const PlayStylesRecommendations = ({
  recommendations,
  player,
  onApplyRecommendation,
  loading,
  onSave,
  saving,
  onShare,
  onClear,
}: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation(["players", "common"]);
  const handleApplyRecommendation = useCallback(
    (recommendation: PlayStylesLabRecommendation) => {
      onApplyRecommendation?.(recommendation);
      setIsOpen(false);
    },
    [onApplyRecommendation],
  );

  const [best, ...rest] = recommendations;

  return (
    <div className="flex w-full flex-col gap-y-4 rounded-lg border border-panel-middleground bg-inner-box p-4 shadow-button-new">
      <div className="flex h-7 items-center">
        <h3 className="text-sm text-dark-secondary leading-6">
          {t("playStylesLab.recommendations.title")}
        </h3>
      </div>

      {recommendations.length === 0 ? (
        <div className="flex h-[186px] w-full items-center justify-center gap-x-2.5 rounded-lg bg-panel-middleground py-2.5 px-3 relative">
          {!loading && (
            <>
              <div className="hidden h-[170px] shrink-0 -mt-1 scale-90 items-center justify-center desktop:flex">
                <div className="w-[111px] text-panel-base">
                  {pitchCardPlaceholder}
                </div>
              </div>
              <div className="flex-1" />
              <div className="flex flex-1 flex-col items-center justify-center gap-y-4 absolute inset-0">
                <p className="text-center text-sm text-dark-tertiary leading-6">
                  {t("playStylesLab.recommendations.empty")}
                </p>
                <div className="flex flex-row flex-wrap items-center justify-center gap-3">
                  <div className="w-20">
                    <Button
                      large={true}
                      variant="primary"
                      icon={
                        saving ? (
                          <Oval width={20} height={20} />
                        ) : (
                          <Save size={20} />
                        )
                      }
                      iconBeforeText
                      label={t("common:save")}
                      onClick={() => onSave?.()}
                      disabled={saving}
                    />
                  </div>
                  <div className="w-20">
                    <Button
                      variant="secondary"
                      icon={<Share size={20} />}
                      iconBeforeText
                      label={t("common:share")}
                      onClick={() => onShare?.()}
                      large={true}
                    />
                  </div>
                  <div className="w-20">
                    <Button
                      variant="tertiary"
                      iconBeforeText
                      label={t("common:reset")}
                      onClick={() => onClear?.()}
                      large={true}
                    />
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      ) : (
        <div className="flex flex-col">
          <PlayStyleRecommendationCard
            recommendation={best}
            player={{
              ...player,
              playStylesPlus: [
                ...player?.playStylesPlus,
                ...best.playStylesPlus,
              ],
            }}
            onSelect={handleApplyRecommendation}
          />
          <div
            className={cn(
              "transition-all transition-discrete duration-300 ease-in-out",
              isOpen
                ? "block h-3 opacity-100 starting:h-0 starting:opacity-0"
                : "hidden h-0 opacity-0",
            )}
          />
          {rest.length > 0 && (
            <div
              className={cn(
                "grid overflow-hidden transition-all duration-300 ease-in-out",
                isOpen
                  ? "grid-rows-[1fr] opacity-100"
                  : "grid-rows-[0fr] opacity-0",
              )}
            >
              <div className="flex min-h-0 flex-col gap-3 overflow-hidden">
                {rest.map((recommendation, index) => (
                  <PlayStyleRecommendationCard
                    key={`${recommendation.playerRoleId}-${index}`}
                    recommendation={recommendation}
                    player={{
                      ...player,
                      playStylesPlus: [
                        ...player?.playStylesPlus,
                        ...recommendation.playStylesPlus,
                      ],
                    }}
                    onSelect={handleApplyRecommendation}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="flex w-full items-center justify-center">
        <button
          onClick={() => setIsOpen((open) => !open)}
          className={cn(
            "flex h-9 cursor-pointer items-center justify-center gap-2 rounded bg-tertiary px-2 py-1 shadow-button-new ",
            rest.length === 0
              ? "opacity-50 cursor-not-allowed bg-disabled"
              : "hover:bg-tertiary-hover",
          )}
          disabled={rest.length === 0}
        >
          <span className="text-xs font-medium text-dark-secondary leading-4">
            {isOpen ? t("common:showLess") : t("common:showMore")}
          </span>
          <span
            className={cn(
              "h-2 w-2 shrink-0 transition-transform duration-300 ease-in-out",
              isOpen && "rotate-180",
            )}
          >
            {arrow}
          </span>
        </button>
      </div>
    </div>
  );
};
